import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลผู้ใช้แบบย่อ
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// Interface สำหรับ RepairMethod
export interface RepairMethod {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by_id?: number;
  created_by?: UserInfo;
  updated_by_id?: number;
  updated_by?: UserInfo;
}

// Interface สำหรับ RepairMethod Form
export interface RepairMethodForm {
  code: string;
  name: string;
  description?: string;
  is_active?: boolean;
}

// Interface สำหรับ RepairMethod List Response
export interface RepairMethodListResponse {
  items: RepairMethod[];
  total: number;
  page: number;
  limit: number;
}

// Interface สำหรับ RepairMethod Query Parameters
export interface GetRepairMethodsParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

const repairMethodService = {
  // ดึงรายการวิธีการแก้ไขทั้งหมด
  async getRepairMethods(params?: GetRepairMethodsParams): Promise<RepairMethodListResponse> {
    try {
      const response = await ApiWithLogging.get('/repair-methods/',
        { params },
        {
          service: 'repairMethodService',
          action: 'view_repair_methods_list',
          entityType: 'repair_method'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching repair methods:', error);
      throw error;
    }
  },

  // ดึงรายการวิธีการแก้ไขที่ใช้งานอยู่ (สำหรับ dropdown)
  async getActiveRepairMethods(): Promise<RepairMethod[]> {
    try {
      const response = await ApiWithLogging.get('/repair-methods/active', {},
        {
          service: 'repairMethodService',
          action: 'view_active_repair_methods',
          entityType: 'repair_method'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching active repair methods:', error);
      throw error;
    }
  },

  // ดึงข้อมูลวิธีการแก้ไขตาม ID
  async getRepairMethodById(id: number): Promise<RepairMethod> {
    try {
      const response = await ApiWithLogging.get(`/repair-methods/${id}`, {},
        {
          service: 'repairMethodService',
          action: 'view_repair_method_detail',
          entityType: 'repair_method',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching repair method by ID:', error);
      throw error;
    }
  },

  // สร้างวิธีการแก้ไขใหม่
  async createRepairMethod(data: RepairMethodForm): Promise<RepairMethod> {
    try {
      const response = await ApiWithLogging.post('/repair-methods/', data, {},
        {
          service: 'repairMethodService',
          action: 'create_repair_method',
          entityType: 'repair_method',
          details: { code: data.code, name: data.name }
        }
      );
      return response;
    } catch (error) {
      console.error('Error creating repair method:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลวิธีการแก้ไข
  // BUG-S162-P1: pass the record's last-seen updated_at as `ifMatch` to enable the backend
  // optimistic-concurrency lock (stale two-tab overwrite → 409). Optional.
  async updateRepairMethod(id: number, data: Partial<RepairMethodForm>, ifMatch?: string): Promise<RepairMethod> {
    try {
      const response = await ApiWithLogging.put(`/repair-methods/${id}`, data, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'repairMethodService',
          action: 'update_repair_method',
          entityType: 'repair_method',
          entityId: id,
          details: { updated_fields: Object.keys(data) }
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating repair method:', error);
      throw error;
    }
  },

  // ลบวิธีการแก้ไข
  async deleteRepairMethod(id: number): Promise<void> {
    try {
      await ApiWithLogging.delete(`/repair-methods/${id}`, {},
        {
          service: 'repairMethodService',
          action: 'delete_repair_method',
          entityType: 'repair_method',
          entityId: id
        }
      );
    } catch (error) {
      console.error('Error deleting repair method:', error);
      throw error;
    }
  },

  // เปิด/ปิดใช้งานวิธีการแก้ไข
  async toggleRepairMethodStatus(id: number): Promise<RepairMethod> {
    try {
      const response = await ApiWithLogging.patch(`/repair-methods/${id}/toggle`, {}, {},
        {
          service: 'repairMethodService',
          action: 'toggle_repair_method_status',
          entityType: 'repair_method',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error toggling repair method status:', error);
      throw error;
    }
  },

  // ส่งออกข้อมูลวิธีการแก้ไขเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้กรองแล้วกดส่งออก
  // จะได้ไฟล์ทั้งระบบโดยไม่มีอะไรเตือน (ไฟล์ที่ได้ต้องตรงกับที่เห็นบนจอ)
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = String(params.is_active);

    const response = await ApiWithLogging.get('/repair-methods/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'repairMethodService',
        action: 'export_repair_methods_excel',
        entityType: 'repair_method'
      }
    );
    return response;
  }
};

export default repairMethodService;
